/**
 * Program classification — the ONE place the UI decides whether a program is
 * the auto-managed General Education program or the University Requirements
 * program.
 *
 * Six components used to carry their own copy of this predicate, and two of
 * them (the audit's program list and the requirements header) lacked the
 * `degree_type === 'GE'` leg — the data field the backend classifies by. The
 * copies only agreed because every institution's GE YAML happens to be named
 * "General Education Requirements" (a convention now pinned by a backend
 * test). Plan-state entries (`academicPlan.majors[]`) carry only
 * `program`/`track`, so the name legs still matter; audit programs carry
 * `degree_type`, `program_key` and `module`; Settings/onboarding shapes carry
 * `name`/`code`/`type`. The predicate accepts all of them.
 *
 * (2026-09-24 gen-ed generality audit, frontend risk #1.)
 */

export const GEN_ED_PROGRAM_NAME = 'General Education Requirements';
export const UNIVERSITY_REQUIREMENTS_PROGRAM_NAME = 'University Requirements';

/** Any of the program shapes the UI passes around. All fields optional. */
export interface ProgramLike {
  program?: string | null;
  program_name?: string | null;
  name?: string | null;
  code?: string | null;
  program_key?: string | null;
  module?: string | null;
  type?: string | null;
  degree_type?: string | null;
}

const displayName = (p: ProgramLike): string =>
  p.program_name || p.program || p.name || '';

/** True for the General Education program in every shape the UI sees. */
export const isGenEdProgram = (p: ProgramLike | null | undefined): boolean => {
  if (!p) return false;
  if (p.degree_type === 'GE' || p.type === 'general_education') return true;
  if (p.program_key === 'GENERAL_EDUCATION_REQUIREMENTS') return true;
  if (p.code === 'GEN_ED' || p.code === 'GENED') return true;
  if (p.module?.includes('General Education')) return true;
  const name = displayName(p) || p.code || '';
  return name.toLowerCase().includes('general education');
};

/** True for the University Requirements program in every shape the UI sees. */
export const isUniversityRequirementsProgram = (
  p: ProgramLike | null | undefined,
): boolean => {
  if (!p) return false;
  if (p.degree_type === 'UR' || p.type === 'university_requirements') return true;
  if (p.program_key === 'UNIVERSITY_REQUIREMENTS') return true;
  return displayName(p) === UNIVERSITY_REQUIREMENTS_PROGRAM_NAME;
};

/** Auto-managed = attached by the platform, not chosen by the student. */
export const isAutoManagedProgram = (p: ProgramLike | null | undefined): boolean =>
  isGenEdProgram(p) || isUniversityRequirementsProgram(p);
